import React, { useRef, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress, LinearProgress, IconButton, Chip } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { storage, db } from '../../../firebase/config';
import { useAuth } from '../../../contexts/AuthContext';
import { useAppFiles } from '../hooks/useAppFiles';
import { APPLAB_ACCENT } from '../constants';

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

const ALLOWED_EXT = new Set([
  '.xlsx', '.xls', '.csv', '.pdf',
  '.ifc', '.bcf', '.bcfzip', '.ids',
  '.json', '.xml',
  '.png', '.jpg', '.jpeg', '.gif', '.svg', '.webp',
  '.txt', '.md',
]);

const MIME_MAP = {
  '.pdf':    'application/pdf',
  '.xlsx':   'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls':    'application/vnd.ms-excel',
  '.csv':    'text/csv',
  '.json':   'application/json',
  '.xml':    'application/xml',
  '.png':    'image/png',
  '.jpg':    'image/jpeg',
  '.jpeg':   'image/jpeg',
  '.gif':    'image/gif',
  '.svg':    'image/svg+xml',
  '.webp':   'image/webp',
  '.txt':    'text/plain',
  '.md':     'text/markdown',
  '.ifc':    'application/x-step',
  '.bcf':    'application/zip',
  '.bcfzip': 'application/zip',
  '.ids':    'application/xml',
};

function fmtSize(bytes) {
  if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  if (bytes >= 1024)        return (bytes / 1024).toFixed(0) + ' KB';
  return bytes + ' B';
}

function extOf(name) {
  const m = name.match(/(\.[^.]+)$/);
  return m ? m[1].toLowerCase() : '';
}

export default function AppFiles({ appId }) {
  const { currentUser } = useAuth();
  const { files, loading } = useAppFiles(appId);
  const [dragging, setDragging]   = useState(false);
  const [uploading, setUploading] = useState(null); // { name, progress }
  const [error, setError]         = useState(null);
  const [deleting, setDeleting]   = useState(null);
  const inputRef = useRef();

  const handleFile = useCallback(async (file) => {
    setError(null);
    const ext = extOf(file.name);
    if (!ALLOWED_EXT.has(ext)) {
      setError(`Filtypen ${ext} är inte tillåten.`);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError('Filen är för stor (max 100 MB).');
      return;
    }

    const storagePath = `applab/apps/${appId}/files/${Date.now()}_${file.name}`;
    const storageRef  = ref(storage, storagePath);
    const contentType = MIME_MAP[ext] || 'application/octet-stream';

    setUploading({ name: file.name, progress: 0 });

    const task = uploadBytesResumable(storageRef, file, { contentType });

    task.on('state_changed',
      snap => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setUploading(u => ({ ...u, progress: pct }));
      },
      err => {
        setError('Uppladdning misslyckades: ' + err.message);
        setUploading(null);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(task.snapshot.ref);
          await addDoc(collection(db, 'applab_apps', appId, 'files'), {
            name:        file.name,
            size:        file.size,
            contentType,
            storagePath,
            downloadURL,
            uploadedBy:  currentUser.uid,
            uploadedAt:  serverTimestamp(),
          });
        } catch (e) {
          setError('Kunde inte spara filinfo: ' + e.message);
        } finally {
          setUploading(null);
        }
      }
    );
  }, [appId, currentUser]);

  const onDrop = useCallback(e => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  async function handleDelete(file) {
    setDeleting(file.id);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/.netlify/functions/applab-file-delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ appId, fileId: file.id }),
      });
      if (!res.ok) {
        const d = await res.json();
        setError(d.error || 'Radering misslyckades');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(null);
    }
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#111', overflow: 'hidden' }}>

      {/* Drop zone */}
      <Box
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        sx={{
          mx: 2, mt: 2, mb: 1.5, p: 2.5,
          border: `2px dashed ${dragging ? APPLAB_ACCENT : 'rgba(255,255,255,0.1)'}`,
          borderRadius: 2,
          bgcolor: dragging ? APPLAB_ACCENT + '0d' : 'rgba(255,255,255,0.02)',
          textAlign: 'center',
          cursor: uploading ? 'default' : 'pointer',
          transition: 'all 0.15s',
          '&:hover': uploading ? {} : { borderColor: APPLAB_ACCENT, bgcolor: APPLAB_ACCENT + '0a' },
          flexShrink: 0,
        }}
      >
        <input ref={inputRef} type="file" style={{ display: 'none' }}
          onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }} />
        {uploading ? (
          <Box>
            <Typography fontSize="0.8rem" sx={{ color: '#888', mb: 1 }}>
              Laddar upp {uploading.name}…
            </Typography>
            <LinearProgress
              variant="determinate"
              value={uploading.progress}
              sx={{ borderRadius: 2, bgcolor: 'rgba(255,255,255,0.06)',
                '& .MuiLinearProgress-bar': { bgcolor: APPLAB_ACCENT } }}
            />
            <Typography fontSize="0.72rem" sx={{ color: '#555', mt: 0.5 }}>{uploading.progress}%</Typography>
          </Box>
        ) : (
          <Box>
            <UploadFileIcon sx={{ fontSize: 28, color: '#444', mb: 0.5 }} />
            <Typography fontSize="0.8rem" sx={{ color: '#666' }}>Dra hit eller klicka för att ladda upp</Typography>
            <Typography fontSize="0.7rem" sx={{ color: '#444', mt: 0.5 }}>
              PDF, Excel, IFC, CSV, bilder m.m. · Max 100 MB
            </Typography>
          </Box>
        )}
      </Box>

      {error && (
        <Box sx={{ mx: 2, mb: 1, px: 1.5, py: 1, bgcolor: '#2e0000', borderRadius: 1.5 }}>
          <Typography fontSize="0.78rem" sx={{ color: '#ef9a9a' }}>{error}</Typography>
        </Box>
      )}

      {/* File list */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={24} sx={{ color: APPLAB_ACCENT }} />
          </Box>
        ) : files.length === 0 ? (
          <Typography fontSize="0.8rem" sx={{ color: '#444', textAlign: 'center', pt: 3 }}>
            Inga filer uppladdade ännu.
          </Typography>
        ) : (
          files.map(file => (
            <Box key={file.id} sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              py: 1.2, px: 1.5, mb: 0.75,
              bgcolor: '#1a1a1a',
              borderRadius: 1.5,
              border: '1px solid rgba(255,255,255,0.06)',
              '&:hover': { borderColor: 'rgba(255,255,255,0.12)' },
            }}>
              <InsertDriveFileIcon sx={{ fontSize: 20, color: '#555', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontSize="0.82rem" fontWeight={600} sx={{ color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {file.name}
                </Typography>
                <Typography fontSize="0.7rem" sx={{ color: '#555' }}>
                  {fmtSize(file.size || 0)}
                  {file.uploadedAt && ` · ${file.uploadedAt.toDate?.().toLocaleDateString('sv-SE') ?? ''}`}
                </Typography>
              </Box>
              <Chip
                label={extOf(file.name).replace('.', '').toUpperCase()}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: '#666', fontSize: '0.6rem', height: 18, flexShrink: 0 }}
              />
              <IconButton
                component="a"
                href={file.downloadURL}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{ color: '#555', '&:hover': { color: APPLAB_ACCENT }, flexShrink: 0 }}
              >
                <DownloadIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDelete(file)}
                disabled={deleting === file.id}
                sx={{ color: '#555', '&:hover': { color: '#ef5350' }, flexShrink: 0 }}
              >
                {deleting === file.id
                  ? <CircularProgress size={14} sx={{ color: '#555' }} />
                  : <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                }
              </IconButton>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}
